// angular dependencies
import { inject, type Signal } from '@angular/core';

// fantaz dependencies
import { DataAccessTournamentsService } from '@fantaz/shared/data-access/api';
import {
  injectQuery,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

/**
 * Fetches the details of a tournament based on the provided tournament URL.
 *
 * This function uses the `DataAccessTournamentsService` to fetch the tournament details
 * and returns a query object that can be used with React Query.
 *
 * @param tournamentUrl - A Signal that provides the URL of the tournament. It can be a string or undefined.
 * @returns A query object that contains the tournament details.
 *
 * The query object has the following properties:
 * - `queryKey`: An array containing the string 'tournament' and the tournament URL.
 * - `queryFn`: A function that fetches the tournament details using the `fetchTournamentDetails` method of the service.
 * - `placeholderData`: Keeps the previous data while fetching new data.
 * - `staleTime`: The time in milliseconds for which the data is considered fresh (60 minutes).
 */
export function tournamentDetailsByUrlQuery(params: {
  url: Signal<string | undefined>;
}) {
  const service = inject(DataAccessTournamentsService);

  return injectQuery(() => ({
    queryKey: ['tournaments', params.url()],
    queryFn: () =>
      lastValueFrom(
        service.fetchTournamentDetails({
          tournamentUrl: params.url()!,
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 60, // 60s minutes
  }));
}
